import { useToast } from '@app/core/notifications';
import { SocialPlatformId } from '@app/modules/social-export/domain/platforms';
import {
  fragmentsDeliveryAPIClientEnableDelivery,
  socialExport,
} from '@mi-front/api-clients';
import { useWebAppCreateFragmentCreation } from '@mi-front/api-v2';
import { PartialRecord } from '@mi-front/core';
import { useIntl } from 'react-intl';
import { apiClient } from '../../../api';
import { routeGenerator } from '../routing/FragmentsRoutes';
import { FragmentCreationResponseDto } from '../fragment.types';
import { CreateDefaultFragmentData, CreateJointFragmentData, Fragment } from '../types';

type CreateFragmentData = (CreateDefaultFragmentData | CreateJointFragmentData) & {
  platformUpload?: PartialRecord<SocialPlatformId, { enabled: boolean; config?: unknown }>;
  doExternalExport?: boolean;
};

interface FragmentCreateOptions {
  /** Called after the fragment is created on the BFF (response is `{ id }`). */
  onSuccess?: (created: FragmentCreationResponseDto) => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export function useFragmentCreate(options?: FragmentCreateOptions) {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const createFragmentMutation = useWebAppCreateFragmentCreation({
    mutation: {},
  });
  const socialExportStart = socialExport.useCreateFragmentUpload({
    mutation: {},
  });

  async function save(data: CreateFragmentData) {
    try {
      const { platformUpload, doExternalExport, ...rest } = data;

      const fragment = await createFragmentMutation.mutateAsync({ data: rest });

      if (doExternalExport) {
        try {
          await fragmentsDeliveryAPIClientEnableDelivery(fragment.id, {});
        } catch {
          notifyError(
            formatMessage({
              id: 'fragments.addScreen.externalExportError',
              defaultMessage: 'No se pudo iniciar el export externo',
            }),
          );
        }
      }

      if (platformUpload) {
        for (const p in platformUpload) {
          const platform = p as SocialPlatformId;
          const data = platformUpload[platform];

          if (data?.enabled) {
            await socialExportStart
              .mutateAsync({
                fragment: fragment.id,
                data: {
                  platform,
                  data: data.config ?? {},
                } as unknown as socialExport.CreateFragmentUploadDto,
              })
              .catch((err: unknown) => {
                console.error(err);
                notifyError(
                  formatMessage({
                    id: 'fragments.addScreen.exportError',
                    defaultMessage: 'No se pudo iniciar el export externo',
                  }),
                );
              });
          }
        }
      }

      if (options?.onSuccess) {
        await options.onSuccess(fragment);
      } else {
        notifySuccess(
          formatMessage({
            id: 'fragments.addScreen.created',
            defaultMessage: 'Fragmento creado',
          }),
          {
            link: {
              text: formatMessage({
                id: 'fragments.addScreen.goTo',
                defaultMessage: 'Ir al fragmento',
              }),
              to: routeGenerator.view(fragment.id),
            },
          },
        );
      }
    } catch (err: unknown) {
      if (options?.onError) {
        options.onError(err);
      } else {
        notifyError(
          formatMessage({
            id: 'fragments.addScreen.createError',
            defaultMessage: 'No se pudo crear el fragmento',
          }),
        );
      }

      throw err;
    }
  }

  return save;
}
